import { useWindowSize } from "hooks/useWindowSize";
import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import Table from "components/DesignSystem/Table/V2/Table";
import { Cross } from "components/icons/Cross";
import { Info } from "components/icons/Info";
import { InfoFilledSmall } from "components/icons/InfoFilledSmall";
import { NoVendors } from "components/icons/NoVendors";
import { DocumentPreviewModal } from "components/PreviewModal";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import React, { useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { setMerchantToMerge } from "store/slices/vendors";
import { CPA_REVIEWED } from "types/Models/vendors";
import { MarkMerchantAsVendor } from "./MarkMerchantAsVendorModal";
import { MarkMultipleMerchantsAsRequired } from "./MarkMultipleMerchantsAsRequired";
import { MerchantTableDropdown } from "./MerchantTableDropdown";
import { MergeMerchants } from "./MergeMerchants";
import VendorColumn, { Form1099Handler, WForm } from "./VendorColumn";
import { VendorTransactions } from "./VendorTransactions";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Contacts } from "components/icons/LeftNav/Books/Contacts";
import { DollerIcon } from "components/icons/DollerIcon";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { DividerLine } from "components/icons/DividerLine";
import { ConfirmRowSelect } from "./BulkActions/ConfirmRowSelect";
import { SOURCE } from "constants/vendor";
import { VendorDefault } from "components/icons/VendorDefault";
import { RelatedPartyIcon } from "components/icons/RelatedPartyIcon";
import { motion } from "framer-motion";
import {
  clearVendorEmailRequestsInChat,
  setVendorEmailRequestsInChat,
} from "store/slices/vendorEmailRequestsInChat";
import { useBulkUpdateVendorsMutation } from "store/apis/vendors";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { VendorBulkActionsDropdown } from "./BulkActions/VendorBulkActionsDropdown";
import { VendorEmailRequestModal } from "./VendorEmailRequestModal";

const MOBILE_COLUMN_WIDTHS = {
  select: "48px",
  logo: "280px",
  transaction_amount: "200px",
  "1099_transaction_amount": "200px",
};

const AnimateRow = ({ row, onAnimateComplete }) => {
  const content = useRef(null);
  const [refAvailable, setRefAvailable] = useState(false);

  return (
    <motion.tr
      className={classNames(
        "t-cursor-pointer t-absolute t-overflow-hidden t-rounded-md t-z-[9999] t-w-14 t-h-14"
      )}
      ref={(ref) => {
        content.current = ref;
        setRefAvailable(true);
      }}
      animate={
        refAvailable
          ? {
              opacity: [null, 0],
              x: [
                null,
                document.body.getBoundingClientRect().right -
                  200 -
                  (content.current?.getBoundingClientRect()?.right ?? 0),
              ],
              y: [
                null,
                document.body.getBoundingClientRect().bottom -
                  32 -
                  (content.current?.getBoundingClientRect()?.bottom || 0),
              ],
            }
          : {}
      }
      transition={{
        type: "tween",
        duration: 0.6,
        ease: "easeInOut",
      }}
      onAnimationComplete={() => {
        onAnimateComplete();
      }}
    >
      {row.getVisibleCells().map((cell) => {
        const merchant = row.original.uuid;

        if (!merchant) {
          return null;
        }

        return (
          <td
            key={cell.id}
            style={{
              width: "100px",
              opacity: merchant ? 1 : 0,
            }}
          >
            <div className="t-flex t-gap-2 t-items-center t-border t-border-solid t-border-neutral-30 t-w-7 t-h-7 t-justify-center t-rounded-full">
              {merchant ? (
                <Avatar src={row.original.name || ""} alt={merchant} />
              ) : (
                <VendorDefault />
              )}
            </div>
          </td>
        );
      })}
    </motion.tr>
  );
};

export const MerchantTable = ({
  merchants,
  groupId,
  showWform,
  totalPages,
  paginationData,
  noMerchant,
  sorting,
  setSorting,
  transactionsWithoutVendorsCount,
  entityId,
  showHeader = true,
  selectedSeason,
  filters = <></>,
  isShowEstimated1099 = true,
  isFromVendors = false,
  isLoading = false,
}) => {
  const {
    open: openMarkAsVendorModal,
    close: closeMarkAsVendorModal,
    isOpen: showMarkAsVendorModal,
  } = useModal();
  const {
    open: openMergeMerchantsModal,
    close: closeMergeMerchantsModal,
    isOpen: showMergeMerchantsModal,
  } = useModal();
  const { isCustomer, isAdmin } = useRoleBasedView();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.EXPENSE
  );
  const dispatch = useDispatch();
  const [bulkUpdateVendors] = useBulkUpdateVendorsMutation();
  const { alertToast, successToast } = useToast();
  const [editMerchantId, setEditMerchantId] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedVendorsForMark, setSelectedVendorsForMark] = useState([]);
  const markFormRequiredConfirmationModal = useModal();
  const { isMobile } = useWindowSize();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { link } = useConstructInternalLink();
  const bulkRequestWForm = useModal();
  const { taskId } = useParams();
  const [animateVendors, setAnimateVendors] = useState([]);

  const onAnimateComplete = () => {
    let vendorsToSendToChat = merchants
      .filter((vendor) => animateVendors.includes(vendor.uuid))
      .map((vendor) => ({
        id: vendor.uuid,
        name: vendor.name,
        email: vendor.email,
        logo_url: vendor.logo,
        season: selectedSeason,
      }));

    dispatch(
      setVendorEmailRequestsInChat({
        vendorEmailRequestsInChat: vendorsToSendToChat,
        entityId: entityId,
        season: selectedSeason,
      })
    );

    setAnimateVendors([]);
    deselectRows();
  };

  const handleRowAmountClick = (rowId) => {
    setExpandedRow((prevRowId) => (prevRowId === rowId ? null : rowId));
  };

  const openMarkAsVendor = (merchant) => {
    setEditMerchantId(merchant.uuid);
    openMarkAsVendorModal();
  };

  const mergeMerchants = () => {
    const merchantsToMerge = table
      .getSelectedRowModel()
      .flatRows.map((merchant) => {
        return merchant.original.uuid;
      });
    dispatch(setMerchantToMerge(merchantsToMerge));
    openMergeMerchantsModal();
  };

  const columnHelper = createColumnHelper();

  const goToDetails = (id) => {
    if (isFromVendors) {
      history.push(link(`${path}/${id}`));
      return;
    }

    const baseUrl = `/books/expenses/vendors/${id}?entity=${entityId}`;
    const url = isCustomer ? baseUrl : `${baseUrl}&company=${groupId}`;
    history.push(url);
  };

  const checkBoxColumn = [
    columnHelper.display({
      id: "select",
      size: 5,
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          indeterminate={row.getIsSomeSelected()}
          onChange={row.getToggleSelectedHandler()}
          key={row.id}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    }),
  ];

  const amount1099 = isShowEstimated1099
    ? [
        columnHelper.accessor("1099_transaction_amount", {
          cell: (info) => {
            const showTransactions = info.row.original?.name === expandedRow;

            const handleRowClick = (e) => {
              e.stopPropagation();
              handleRowAmountClick(info.row.original?.name || "");
            };

            return (
              <div
                className="t-w-full t-flex t-justify-end t-text-subtext t-text-text-60 t-pr-5 t-group"
                onClick={handleRowClick}
              >
                <button
                  className={classNames("all:unset t-text-body t-ml-auto", {
                    "hover:!t-underline group-hover:t-text-purple":
                      !showTransactions,
                    "!t-underline t-text-purple": showTransactions,
                  })}
                >
                  <AmountSuperScript amount={Math.abs(info.getValue())} />
                </button>
              </div>
            );
          },
          header: (
            <div className="t-w-full t-flex t-justify-end t-gap-1">
              <span>1099 AMOUNT</span>
              <ToolTip text="Cumulative value of all transactions with the vendor excluding credit card payments">
                <span>
                  <InfoFilledSmall color="currentColor" />
                </span>
              </ToolTip>
            </div>
          ),
          size: 18,
        }),
      ]
    : [];

  const wFormColumn = isShowEstimated1099
    ? [
        columnHelper.accessor("w_form_document", {
          enableSorting: false,
          cell: (info) => {
            return (
              <div
                onClick={(e) => e.stopPropagation()}
                className="t-cursor-default"
              >
                <WForm
                  groupId={groupId}
                  vendor={info.row.original}
                  season={selectedSeason}
                />
              </div>
            );
          },
          header: "W FORM",
          id: "wFormDocument",
          size: 28,
        }),
      ]
    : [];

  const form1099 = isShowEstimated1099
    ? [
        columnHelper.display({
          id: "form_1099_document?.payers_document",
          header: (
            <div className="t-text-center t-w-full t-text-30 t-text-Subtext-sm">
              1099
            </div>
          ),
          enableSorting: false,
          size: 15,
          cell: (info) => {
            return (
              <div
                onClick={(e) => e.stopPropagation()}
                className="t-cursor-default"
              >
                <Form1099Handler
                  entityId={entityId}
                  vendor={info.row.original}
                  season={selectedSeason}
                  groupId={groupId}
                />
              </div>
            );
          },
        }),
      ]
    : [];

  const totalTxnAmount = isShowEstimated1099
    ? []
    : [
        columnHelper.accessor("transaction_amount", {
          cell: (info) => {
            const showTransactions = info.row.original?.name === expandedRow;
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowAmountClick(info.row.original?.name || "");
                }}
                className={classNames(
                  "all:unset t-text-body t-ml-auto t-flex t-justify-end t-pr-5",
                  {
                    "hover:!t-underline hover:t-text-purple t-text-text-60":
                      !showTransactions,
                    "!t-underline t-text-purple": showTransactions,
                  }
                )}
              >
                <AmountSuperScript amount={Math.abs(info.getValue())} />
              </button>
            );
          },
          header: (
            <div className="t-w-full t-flex t-justify-end t-gap-1">
              <span>TOTAL TRANSACTED AMOUNT</span>
              <ToolTip
                text={
                  <>
                    Sum of all billed or un-billed transactions
                    <br />
                    including payments made and received
                  </>
                }
              >
                <span>
                  <InfoFilledSmall color="currentColor" />
                </span>
              </ToolTip>
            </div>
          ),
          size: 40,
        }),
      ];

  const sourceColumn =
    isShowEstimated1099 || isMobile
      ? []
      : [
          columnHelper.accessor("source", {
            header: () => (
              <div className="t-flex t-justify-center t-flex-1">SOURCE</div>
            ),
            size: 10,
            enableSorting: false,
            cell: (info) => {
              const source = info.getValue();
              return (
                <div className="t-flex t-justify-center">
                  <ConditionalToolTip
                    condition={
                      <div className="t-lowercase first-letter:t-capitalize">
                        {source}
                      </div>
                    }
                  >
                    <div
                      className="t-rounded-full t-bg-white t-overflow-hidden t-size-6 t-text-text-30 t-border-neutral-10 t-border t-border-solid t-text-center"
                      role="img"
                    >
                      {source === SOURCE.MANUAL && <Contacts />}
                      {source === SOURCE.TRANSACTION && <DollerIcon />}
                    </div>
                  </ConditionalToolTip>
                </div>
              );
            },
          }),
        ];

  const columns = useMemo(
    () => [
      ...checkBoxColumn,
      columnHelper.accessor("logo", {
        enableSorting: false,
        cell: (info) => {
          const merchantName = info.row.original.name;

          return (
            <div className="t-flex t-gap-2 t-items-center">
              <Avatar
                src={info.getValue()}
                alt={merchantName}
                variant="primary"
                size="regular"
              />
              <div>
                <div className="t-flex t-items-center t-gap-1">
                  <button
                    className={classNames(
                      "all:unset t-text-subtext t-text-text-60 t-group  hover:t-underline hover:t-text-purple-50 t-cursor-auto"
                    )}
                    onClick={(e) => goToDetails(info.row.original.uuid)}
                  >
                    {merchantName}
                  </button>
                  {info.row.original.is_related_party && (
                    <ToolTip text="Related party">
                      <span>
                        <RelatedPartyIcon />
                      </span>
                    </ToolTip>
                  )}
                </div>
                <div>
                  <VendorColumn.Email
                    info={info}
                    editMerchantId={editMerchantId}
                    setEditMerchantId={setEditMerchantId}
                  />
                </div>
              </div>
            </div>
          );
        },
        header: "VENDOR",
        size: 35,
      }),

      ...amount1099,
      ...wFormColumn,
      ...form1099,

      ...totalTxnAmount,
      ...sourceColumn,

      columnHelper.display({
        header: "",
        id: "actions",
        enableSorting: false,
        cell: (info) => {
          const isManualMerchant = info.row.original.source === "MANUAL";
          const isVendor =
            info.row.original.is_w_form_required !== "NOT_REQUIRED";
          const showOption = showWform
            ? info.row.original?.is_w_form_required === CPA_REVIEWED
            : true;

          if (!showOption) {
            return;
          }

          return (
            <div className="t-flex t-justify-end t-w-full">
              <MerchantTableDropdown
                vendor={info.row.original}
                openMarkAsVendor={() => openMarkAsVendor(info.row.original)}
                merchantId={info.row.original.uuid}
                season={info.row.original?.w_form_season}
                groupId={groupId}
                isManualMerchant={isManualMerchant}
                isVendor={isVendor}
                showTransactions={setExpandedRow}
                expandedRow={expandedRow}
                vendorName={info.row.original.name}
                form1099Status={info.row.original.form_1099_status}
                taskId={taskId}
                entityId={entityId}
                hasEmail={!!info.row.original.email}
                setAnimateVendors={setAnimateVendors}
                selectedSeason={isFromVendors ? undefined : selectedSeason}
              />
            </div>
          );
        },
        size: showWform ? 5 : 10,
      }),
    ],
    [
      entityId,
      groupId,
      isCustomer,
      selectedSeason,
      showWform,
      expandedRow,
      isShowEstimated1099,
      isMobile,
    ]
  );

  const getColumnWidth = (column) =>
    isMobile && MOBILE_COLUMN_WIDTHS[column.id]
      ? MOBILE_COLUMN_WIDTHS[column.id]
      : `${column.getSize()}%`;

  const table = useReactTable({
    data: merchants,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    enableMultiSort: false,
    enableSortingRemoval: false,
    defaultColumn: {
      minSize: 1,
    },
  });

  const markFormRequired = async () => {
    const selectedMerchants = table
      .getSelectedRowModel()
      .flatRows.map((merchant) => {
        return merchant.original.uuid;
      });
    setSelectedVendorsForMark(selectedMerchants);
    markFormRequiredConfirmationModal.open();
  };

  const markSelectedAsRelatedParty = async () => {
    const vendor_ids = table
      .getSelectedRowModel()
      .flatRows.map((row) => row.original.uuid);
    try {
      await bulkUpdateVendors({
        groupId,
        vendor_ids,
        update_data: { is_related_party: true },
      }).unwrap();
      successToast({ message: "Vendors marked as related party" });
      table.resetRowSelection();
    } catch (error) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const deselectRows = () => {
    table.resetRowSelection();
    dispatch(clearVendorEmailRequestsInChat);
  };

  const markSelectedVendors = async () => {
    const selectedVendors = table
      .getSelectedRowModel()
      .flatRows.map((vendor) => {
        return vendor.original.uuid;
      });
    setAnimateVendors(selectedVendors);
  };

  const handleResetRowSelection = () => {
    setSelectedVendorsForMark([]);
    table.resetRowSelection();
  };

  const handleSuccess = () => {
    handleResetRowSelection();
    markFormRequiredConfirmationModal.close();
  };

  const selectedRows = table.getSelectedRowModel().flatRows;
  const wFormExistRowCount = selectedRows.reduce(
    (acc, row) => (acc += row.original.w_form_document ? 1 : 0),
    0
  );

  const canMerge = selectedRows.length > 1 && isFromVendors;
  const canMarkFormRequired = isAdmin && !!selectedSeason;
  const canRequestEmail =
    isAdmin && selectedRows.every((row) => !row.original.email);

  const someEmailMissing = selectedRows.some(
    (row) => !row.original.email || row.original.email.trim() === ""
  );

  return (
    <>
      {showHeader && (
        <>
          <div className="t-flex t-items-center sm:t-flex-col sm:t-items-stretch sm:t-gap-2">
            {filters}
            {paginationData && (
              <div className="t-ml-auto sm:t-ml-0 sm:t-self-end">
                <PaginationHandler
                  page_num={paginationData.currentPage}
                  per_page={paginationData.itemsPerPage}
                  total_count={paginationData.totalItemCount}
                  total_pages={totalPages}
                  isLoading={isLoading}
                />
              </div>
            )}
          </div>
        </>
      )}
      {noMerchant ? (
        <>
          <div className="t-pt-6">
            <EmptyScreen text="No vendors found">
              <NoVendors />
            </EmptyScreen>
          </div>
        </>
      ) : (
        <>
          <div className="t-relative sm:t-w-full sm:t-max-w-full sm:t-overflow-x-hidden">
            <Table.Container
              layout="fixed"
              className={classNames(
                "t-overflow-auto t-mb-28 t-rounded-tr-lg t-rounded-tl-lg"
              )}
            >
              <Table.Content className="t-w-full">
                <Table.Head>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Table.HeadRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <Table.HeadCell
                          className={classNames(
                            "t-text-subtext-sm t-uppercase t-group",
                            { "sm:!t-pr-4": header.column.id === "select" }
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                          key={header.id}
                          style={{ width: getColumnWidth(header.column) }}
                          {...(header.column.getCanSort()
                            ? { role: "button" }
                            : {})}
                        >
                          <span className="t-flex t-gap-1 t-items-center">
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            {header.column.getCanSort() && (
                              <Table.HeadCellSort
                                nextSortOrder={header.column.getNextSortingOrder()}
                                isSorted={header.column.getIsSorted()}
                              />
                            )}
                          </span>
                        </Table.HeadCell>
                      ))}
                    </Table.HeadRow>
                  ))}
                </Table.Head>
                <Table.Body>
                  {table.getRowModel().rows.map((row) => (
                    <React.Fragment key={row.id}>
                      <Table.Row
                        onRowClick={() => goToDetails(row.original.uuid)}
                        className={classNames({
                          "t-cursor-pointer": isFromVendors,
                          "t-cursor-auto": !isFromVendors,
                        })}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            style={{ width: getColumnWidth(cell.column) }}
                            className={classNames("t-p-2", {
                              "sm:!t-pr-4": cell.column.id === "select",
                            })}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </Table.Row>
                      {expandedRow === row.original.name && (
                        <Table.Row className="!t-border-b-0">
                          <Table.Cell
                            colSpan={row.getVisibleCells().length}
                            className="t-pl-[3%] !t-pb-0"
                          >
                            <VendorTransactions
                              vendorName={expandedRow}
                              season={selectedSeason}
                              entityId={entityId}
                            />
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {animateVendors.includes(row.original.uuid) && (
                        <AnimateRow
                          onAnimateComplete={onAnimateComplete}
                          row={row}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.Container>
          </div>

          <SelectedFloatingControl>
            {table.getSelectedRowModel().flatRows.length > 0 && (
              <div className="t-flex t-gap-3 t-items-center sm:t-flex-wrap sm:t-justify-center sm:t-gap-2">
                <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
                  {table.getSelectedRowModel().flatRows.length} selected
                </span>
                <span className="t-text-neutral-10 t-h-full">
                  <DividerLine />
                </span>
                {isMobile ? (
                  <VendorBulkActionsDropdown
                    hasWritePermission={hasWritePermission}
                    canMerge={canMerge}
                    canMarkFormRequired={canMarkFormRequired}
                    canRequestEmail={canRequestEmail}
                    isRequestWFormDisabled={wFormExistRowCount > 0}
                    onMerge={mergeMerchants}
                    onMarkFormRequired={markFormRequired}
                    onRequestWForm={bulkRequestWForm.open}
                    onRequestEmail={markSelectedVendors}
                    onMarkRelatedParty={markSelectedAsRelatedParty}
                  />
                ) : (
                  <>
                    {table?.getSelectedRowModel?.()?.flatRows.length > 1 &&
                      isFromVendors &&
                      (hasWritePermission ? (
                        <Button size="small" onClick={mergeMerchants}>
                          Merge
                        </Button>
                      ) : (
                        <LockedButton text="Merge" />
                      ))}
                    {isAdmin && selectedSeason && (
                      <Button size="xsmall" onClick={markFormRequired}>
                        Mark form required
                      </Button>
                    )}
                    {hasWritePermission ? (
                      <ConditionalToolTip
                        condition={
                          wFormExistRowCount > 0 &&
                          `${wFormExistRowCount} contractor already has a w form requested`
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            disabled={wFormExistRowCount > 0}
                            onClick={bulkRequestWForm.open}
                          >
                            Request W form
                          </Button>
                        </span>
                      </ConditionalToolTip>
                    ) : (
                      <LockedButton text="Request W form" />
                    )}
                    {table.getSelectedRowModel().flatRows.length > 0 &&
                      isAdmin &&
                      table
                        .getSelectedRowModel()
                        .flatRows.every((row) => !row.original.email) && (
                        <Button size="xsmall" onClick={markSelectedVendors}>
                          Request Email Address
                        </Button>
                      )}

                    {hasWritePermission ? (
                      <Button size="small" onClick={markSelectedAsRelatedParty}>
                        Mark as related party
                      </Button>
                    ) : (
                      <LockedButton text="Mark as related party" />
                    )}
                  </>
                )}
                <Button
                  customType="ghost_icon"
                  size="xsmall"
                  onClick={handleResetRowSelection}
                >
                  <Cross />
                </Button>
              </div>
            )}
          </SelectedFloatingControl>
        </>
      )}
      <DocumentPreviewModal />

      <ConfirmRowSelect
        table={table}
        {...bulkRequestWForm}
        close={bulkRequestWForm.close}
        groupId={groupId}
        selectedRows={table
          .getSelectedRowModel()
          .flatRows.map(({ original }) => original)}
        season={selectedSeason}
        clearRowSelection={handleResetRowSelection}
        someEmailMissing={someEmailMissing}
      />

      <MarkMerchantAsVendor
        groupId={groupId}
        show={showMarkAsVendorModal}
        closeModal={closeMarkAsVendorModal}
        merchantId={editMerchantId}
        season={selectedSeason}
      />
      <MergeMerchants
        show={showMergeMerchantsModal}
        closeModal={closeMergeMerchantsModal}
        groupId={groupId}
        onSuccess={() => table.resetRowSelection()}
        merchants={merchants}
      />
      <MarkMultipleMerchantsAsRequired
        isOpen={markFormRequiredConfirmationModal.isOpen}
        close={markFormRequiredConfirmationModal.close}
        entityId={entityId}
        merchantIds={selectedVendorsForMark}
        season={selectedSeason}
        onSuccess={handleSuccess}
      />
    </>
  );
};
